import { createContext, Dispatch, SetStateAction, useCallback, useContext, useState } from 'react';

import { ShipmentCarrierWithMoreOptions, ShipmentStep } from '@/models/shipment';
import { useLocalStorageState } from '@/services/localStorage';
import { useDebouncedSearchParamState } from '@/utils/useDebouncedState';
import { useSyncSearchParams } from '@/utils/useSearchParams';

type SortConfig = {
  field: 'reference' | 'statusDueAt';
  order: 'asc' | 'desc';
};

type FiltersState = {
  tab: {
    tab: 'to-do' | 'waiting' | 'all';
    setTab: (tab: 'to-do' | 'waiting' | 'all') => void;
  };
  page: {
    page: number;
    setPage: (page: number) => void;
  };
  search: {
    search: string;
    debouncedSearch: string;
    debouncedSetSearch: (search: string) => void;
  };
  steps: {
    steps: ShipmentStep[];
    setSteps: (steps: ShipmentStep[]) => void;
  };
  carriers: {
    carriers: ShipmentCarrierWithMoreOptions[];
    setCarriers: (carriers: ShipmentCarrierWithMoreOptions[]) => void;
  };
  origins: {
    origins: { id: string; name: string }[];
    setOrigins: (origins: { id: string; name: string }[]) => void;
  };
  destinations: {
    destinations: { id: string; name: string }[];
    setDestinations: (destinations: { id: string; name: string }[]) => void;
  };
  activeFiltersCount: number;
  clearFilters: () => void;
  sort: {
    sort: SortConfig;
    setSort: Dispatch<SetStateAction<SortConfig>>;
  };
};

export const FiltersStateContext = createContext<FiltersState>({} as FiltersState);

export const useFiltersState = ({
  canAccessTodoTab,
}: {
  canAccessTodoTab: boolean;
}): FiltersState => {
  const [tab, setTab] = useLocalStorageState<'to-do' | 'waiting' | 'all'>(
    'shipments:filters:tab',
    canAccessTodoTab ? 'to-do' : 'waiting'
  );

  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), [setPage]);

  const [search, debouncedSearch, debouncedSetSearch, syncSearch] =
    useDebouncedSearchParamState<string>('shipments', 'search', '', 300, resetPage);

  const [steps, setSteps] = useLocalStorageState<ShipmentStep[]>('shipments:filters:steps', []);

  const [carriers, setCarriers] = useLocalStorageState<ShipmentCarrierWithMoreOptions[]>(
    'shipments:filters:carriers',
    []
  );

  const [origins, setOrigins] = useLocalStorageState<{ id: string; name: string }[]>(
    'shipments:filters:origins',
    []
  );

  const [destinations, setDestinations] = useLocalStorageState<{ id: string; name: string }[]>(
    'shipments:filters:destinations',
    []
  );

  const [sort, setSort] = useLocalStorageState<SortConfig>('shipments:filters:sort', {
    field: 'statusDueAt',
    order: 'asc',
  });

  useSyncSearchParams([syncSearch]);

  return {
    tab: {
      tab,
      setTab: (tab) => {
        setTab(tab);
        resetPage();
      },
    },
    page: {
      page,
      setPage,
    },
    search: {
      search,
      debouncedSearch,
      debouncedSetSearch,
    },
    steps: {
      steps,
      setSteps: (steps) => {
        setSteps(steps);
        resetPage();
      },
    },
    carriers: {
      carriers,
      setCarriers: (carriers) => {
        setCarriers(carriers);
        resetPage();
      },
    },
    origins: {
      origins,
      setOrigins: (origins) => {
        setOrigins(origins);
        resetPage();
      },
    },
    destinations: {
      destinations,
      setDestinations: (destinations) => {
        setDestinations(destinations);
        resetPage();
      },
    },
    activeFiltersCount: carriers.length + steps.length + origins.length + destinations.length,
    clearFilters: () => {
      setSteps([]);
      setCarriers([]);
      setOrigins([]);
      setDestinations([]);
      resetPage();
    },
    sort: {
      sort,
      setSort: (newSort) => {
        setSort(newSort);
        resetPage();
      },
    },
  };
};

export const useFilters = () => {
  return useContext(FiltersStateContext);
};
